/**
 * Editorial Slider - Auto-rotating carousel with thumbnail navigation
 */
(function() {
  'use strict';

  class EditorialSlider {
    constructor(container) {
      this.container = container;
      this.slideDuration = parseInt(container.dataset.slideDuration) || 6;
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.isPaused = false;
      this.isUserInteracting = false;

      // Get all slides
      this.thumbnails = container.querySelectorAll('.editorial-slider__thumb');
      this.slideWrappers = container.querySelectorAll('.editorial-slider__slide-wrapper');
      this.slideContents = container.querySelectorAll('.editorial-slider__slide-content');
      this.circles = container.querySelectorAll('.editorial-slider__circle');

      if (this.thumbnails.length === 0) {
        return; // No slides to manage
      }

      this.totalSlides = this.thumbnails.length;

      // Initialize
      this.init();
    }

    init() {
      // Set up thumbnail click handlers
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          // If clicking the active thumbnail, toggle pause/play
          if (index === this.currentIndex) {
            if (this.isPaused) {
              this.resume();
              this.isUserInteracting = false;
            } else {
              this.pause();
              this.isUserInteracting = true;
              // Reset user interaction flag after a delay
              setTimeout(() => {
                this.isUserInteracting = false;
              }, 3000);
            }
          } else {
            // If clicking a different thumbnail, go to that slide
            this.isUserInteracting = true;
            this.goToSlide(index);
            // Reset user interaction flag after a delay
            setTimeout(() => {
              this.isUserInteracting = false;
            }, 3000);
          }
        });
      });

      // Start autoplay
      this.startAutoplay();
      
      // Initialize the first slide's icon (should show pause since it's playing)
      this.updatePlayPauseIcon();
    }
    
    updatePlayPauseIcon() {
      const activeThumb = this.thumbnails[this.currentIndex];
      if (activeThumb) {
        const playIcon = activeThumb.querySelector('.editorial-slider__playIcon');
        const pauseIcon = activeThumb.querySelector('.editorial-slider__pauseIcon');
        if (playIcon && pauseIcon) {
          if (this.isPaused) {
            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
          } else {
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
          }
        }
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides) {
        return;
      }

      // Clear existing timer
      this.clearAutoplay();

      // Update current index
      this.currentIndex = index;

      // Update thumbnails
      this.thumbnails.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('editorial-slider__thumb--active', isActive);
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');

        // Update play/pause icons
        const playIcon = thumb.querySelector('.editorial-slider__playIcon');
        const pauseIcon = thumb.querySelector('.editorial-slider__pauseIcon');
        if (isActive) {
          // Show pause icon when playing, play icon when paused
          if (playIcon && pauseIcon) {
            if (this.isPaused) {
              playIcon.style.display = 'block';
              pauseIcon.style.display = 'none';
            } else {
              playIcon.style.display = 'none';
              pauseIcon.style.display = 'block';
            }
          }
        } else {
          // Hide both icons for inactive thumbnails
          if (playIcon && pauseIcon) {
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'none';
          }
        }

        // Update circle animation
        const circle = thumb.querySelector('.editorial-slider__circle');
        if (circle) {
          if (isActive) {
            circle.classList.add('editorial-slider__circle--animate');
            if (this.isPaused) {
              circle.classList.add('editorial-slider__circle--paused');
            } else {
              circle.classList.remove('editorial-slider__circle--paused');
            }
            // Reset animation
            circle.style.animation = 'none';
            setTimeout(() => {
              circle.style.animation = '';
            }, 10);
          } else {
            circle.classList.remove('editorial-slider__circle--animate', 'editorial-slider__circle--paused');
            circle.style.strokeDasharray = '0px 1px';
          }
        }
      });

      // Update slide wrappers (images) - use data-slide-index to match correctly
      this.slideWrappers.forEach((wrapper) => {
        const slideIndexAttr = wrapper.getAttribute('data-slide-index');
        if (slideIndexAttr !== null) {
          const slideIndex = parseInt(slideIndexAttr, 10);
          const isActive = slideIndex === index;
          if (isActive) {
            wrapper.classList.add('editorial-slider__slide-wrapper--active');
          } else {
            wrapper.classList.remove('editorial-slider__slide-wrapper--active');
          }
        }
      });

      // Update slide contents (text) - use data-slide-index to match correctly
      this.slideContents.forEach((content) => {
        const slideIndexAttr = content.getAttribute('data-slide-index');
        if (slideIndexAttr !== null) {
          const slideIndex = parseInt(slideIndexAttr, 10);
          const isActive = slideIndex === index;
          if (isActive) {
            content.classList.add('editorial-slider__slide-content--active');
          } else {
            content.classList.remove('editorial-slider__slide-content--active');
          }
        }
      });

      // Restart autoplay if not paused
      if (!this.isPaused && !this.isUserInteracting) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.clearAutoplay();
      this.isPaused = false;

      this.autoplayTimer = setTimeout(() => {
        if (!this.isPaused && !this.isUserInteracting) {
          const nextIndex = (this.currentIndex + 1) % this.totalSlides;
          this.goToSlide(nextIndex);
        }
      }, this.slideDuration * 1000);
    }

    pause() {
      this.isPaused = true;
      this.clearAutoplay();

      // Pause circle animation
      const activeCircle = this.thumbnails[this.currentIndex]?.querySelector('.editorial-slider__circle--animate');
      if (activeCircle) {
        activeCircle.classList.add('editorial-slider__circle--paused');
      }

      // Update play/pause icon
      this.updatePlayPauseIcon();
    }

    resume() {
      if (this.isPaused) {
        this.isPaused = false;

        // Resume circle animation
        const activeCircle = this.thumbnails[this.currentIndex]?.querySelector('.editorial-slider__circle--animate');
        if (activeCircle) {
          activeCircle.classList.remove('editorial-slider__circle--paused');
        }

        // Update play/pause icon
        this.updatePlayPauseIcon();

        // Restart autoplay
        this.startAutoplay();
      }
    }

    clearAutoplay() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    destroy() {
      this.clearAutoplay();
      // Remove event listeners would be handled by garbage collection
    }
  }

  // Initialize all editorial sliders on the page
  function initEditorialSliders() {
    const containers = document.querySelectorAll('.editorial-slider__container');
    containers.forEach(container => {
      // Skip if already initialized
      if (container.editorialSlider) {
        return;
      }

      container.editorialSlider = new EditorialSlider(container);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEditorialSliders);
  } else {
    initEditorialSliders();
  }

  // Re-initialize when sections are loaded dynamically (Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId) {
        const section = document.getElementById(event.detail.sectionId);
        if (section && section.classList.contains('editorial-slider__container')) {
          section.editorialSlider = new EditorialSlider(section);
        }
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      if (event.detail.sectionId) {
        const section = document.getElementById(event.detail.sectionId);
        if (section && section.editorialSlider) {
          section.editorialSlider.destroy();
          delete section.editorialSlider;
        }
      }
    });
  }
})();

